import { Component, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { Utils } from 'app/utils';
import { ExportFileTypeModel } from 'app/models/export-file-type.model';
import { ExportFileTypeEnum, XpressUserRoleEnum } from 'app/models';
import { ComboItemDto } from 'app/merchant/partners-portal/interfaces/combo-item.interface';
import { TourBookingStatus } from 'app/ops-tower/tour-bookings/tour-bookng-status.enum';
import { AppDataService, TourOperatorService } from 'app/services';
import moment from 'moment';
import { BookingByIdResponse } from 'app/experience/models/bookings/booking-by-id-response.model';
import {
  TourBookingDetailsComponent
} from 'app/experience/pages/booking-oversight/tour-booking-details/tour-booking-details.component';
import { MatDialog } from '@angular/material/dialog';

interface Query {
  skip: number;
  take: number;
  sortField: string | string[];
  sortOrder: number;
}

@Component({
  selector: 'tour-bookings',
  providers: [MessageService],
  templateUrl: './tour-bookings.component.html',
  styleUrl: './tour-bookings.component.scss',
})
export class TourBookingsComponent implements OnDestroy {
  query: Query = {
    skip: 0,
    take: 12,
    sortField: 'Id',
    sortOrder: -1
  };

  statuses: ComboItemDto[] = [
    {Id: TourBookingStatus.Pending, Name: 'Pending'},
    {Id: TourBookingStatus.Confirmed, Name: 'Confirmed'},
    {Id: TourBookingStatus.Completed, Name: 'Completed'},
    {Id: TourBookingStatus.Cancelled, Name: 'Cancelled'}
  ];

  protected readonly XpressUserRoleEnum = XpressUserRoleEnum;
  bookings: any[] = [];
  totalRecords: number = 0;
  loading: boolean = false;
  rowsPerPageOptions: Array<number> = [12, 25, 50, 100];
  searchTimer: number = 0;

  public filter: {
    startDate: Date | null;
    endDate: Date | null;
    id: number | null;
    customerName: string;
    tourName: string;
    operatorName: string;
    scheduledAt: Date | null;
    guestCount: number | null;
    amount: number | null;
    redeemed: string | null;
    status: number | null;
  } = {
    startDate: null,
    endDate: null,
    id: null,
    customerName: '',
    tourName: '',
    operatorName: '',
    scheduledAt: null,
    guestCount: null,
    amount: null,
    redeemed: null,
    status: null,
  };

  public showExportSidebar: boolean = false;
  public exportLoading: boolean = false;
  public exportFileTypes = ExportFileTypeModel.ExportFileTypes;
  public exportData: { fileType: ExportFileTypeEnum | null } = { fileType: null };

  constructor(public appData: AppDataService,
              private messageService: MessageService,
              private dialog: MatDialog,
              private tourOperatorService: TourOperatorService) {}

  public handleLazyLoad(event: TableLazyLoadEvent): void {
    this.query.skip = event.first ?? 0;
    this.query.take = event.rows ?? this.query.take;

    if (event.sortField) {
      this.query.sortField = event.sortField;
      this.query.sortOrder = event.sortOrder ?? 1;
    }

    Promise.resolve().then(() => this.getTourBookings());
  }

  getRequestDto(extra?: any) {
    return {
      SortField: this.query.sortField,
      SortOrder: this.query.sortOrder,
      StartDate: this.filter.startDate ? moment(this.filter.startDate).format('YYYY-MM-DD') : null,
      EndDate: this.filter.endDate ? moment(this.filter.endDate).format('YYYY-MM-DD') : null,
      Id: this.filter.id,
      CustomerName: this.filter.customerName,
      TourName: this.filter.tourName,
      OperatorName: this.filter.operatorName,
      ScheduledAt: this.filter.scheduledAt ? moment(this.filter.scheduledAt).format('YYYY-MM-DD') : null,
      GuestCount: this.filter.guestCount,
      Amount: this.filter.amount,
      Redeemed: !Utils.isNullOrEmpty(this.filter.redeemed ?? '') ? this.filter.redeemed === 'Yes' : this.filter.redeemed,
      Status: this.filter.status,
      ...extra
    };
  }

  updateStatus(ev: any, booking: any) {
    if (ev.Name === booking.Status) {
      this.messageService.add({ severity: 'warn', summary: 'No changes detected', detail: 'Booking status is identical to current.' });
      return;
    }

    this.loading = true;

    const dto = {
      OrderId: booking.Id,
      StatusId: ev.Id,
    };

    this.tourOperatorService.updateBookingStatus(dto, this.messageService)
        .then(data => {
          this.loading = false;
          if (!data) {
            return;
          }

          if (!data.Success) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
            return;
          }

          this.getTourBookings();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Status successfully updated' });
        })
        .catch(err => {
          this.loading = false;
          throw err;
        });
  }

  onRowClick(bookingId: number) {
    this.getBookingById(bookingId);
  }

  showTourBookingDetails(bookingDto: BookingByIdResponse) {
    this.dialog.open(TourBookingDetailsComponent, {
      width: '685px',
      maxWidth: '90vw',
      data: bookingDto,
      disableClose: false,
      panelClass: 'add-operator-dialog'
    }).afterClosed().subscribe(result => {
      if (result) {
        this.getTourBookings();
      }
    });
  }

  getBookingById(id: number): any {
    this.loading = true;

    const dto = {
      Id: id,
    };
    this.tourOperatorService.getBookingById(dto, this.messageService)
        .then(data => {
          if (!data || !data.Success) {
            this.loading = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: data?.ErrorMessage });
            return;
          }
          this.showTourBookingDetails(data);
        })
        .catch((err: any) => {
          throw err;
        })
        .finally(() => {
          this.loading = false;
        });
  }

  getTourBookings(): void {
    this.loading = true;
    const dto = this.getRequestDto({ Skip: this.query.skip, Take: this.query.take });

    this.tourOperatorService.getTourBookings(dto, this.messageService)
        .then(data => {
          if (!data) {
            return;
          }

          if (!data.Success) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: data?.ErrorMessage });
            return;
          }

          this.totalRecords = data.TotalRecords;
          this.bookings = data.Items;
        })
        .catch(err => {
          throw err;
        })
        .finally(() => this.loading = false);
  }

  public handleFilterChange(): void {
    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }

    this.searchTimer = window.setTimeout(() => {
      this.query.skip = 0;
      this.getTourBookings();
      this.searchTimer = 0;
    }, 1500);
  }

  public openExportSidebar(): void {
    this.showExportSidebar = true;
  }

  public changedVisibleExportSidebar(visible: boolean): void {
    this.showExportSidebar = visible;
  }

  export() {
    if (!this.exportData.fileType) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select file type' });
      return;
    }

    this.exportLoading = true;
    const dto = this.getRequestDto({ FileType: this.exportData.fileType });

    this.tourOperatorService.exportBookings(dto, this.messageService)
        .then(data => {
          if (!data) {
            return;
          }
          Utils.saveFileResponse(data, 'Tour Booking report ' + new Date().toLocaleString(), this.exportData.fileType);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Success' });
          this.changedVisibleExportSidebar(false);
        })
        .catch(err => {
            const message =
                err instanceof Error
                    ? err.message
                    : typeof err === 'string'
                        ? err
                        : 'Unexpected error occurred';
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: message
            });

            throw err;
          })
        .finally(() => this.exportLoading = false);
  }

  ngOnDestroy(): void {
    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = 0;
    }
  }
}
